import type { LiveState } from '@/hooks/use-activity-feed';
import { cn } from '@/lib/utils';

const LABEL: Record<LiveState, string> = {
  connecting: 'Connecting',
  live: 'Live',
  reconnecting: 'Reconnecting',
};

/**
 * The stream's state, as a dot and a word.
 *
 * The word is not decoration: "Live" and "Reconnecting" are different promises
 * about what the numbers on the page mean, and `aria-live="polite"` is what makes
 * the difference reach somebody who is not looking at the dot. Only the live
 * state breathes; reconnecting goes amber and still, because a pulse on a broken
 * connection is a lie.
 */
export function LiveDot({ state }: { state: LiveState }) {
  return (
    <span
      className="text-fg-3 flex items-center gap-1.5 text-2xs"
      aria-live="polite"
      title={
        state === 'live' ? 'Streaming events as they happen' : 'The event stream dropped; retrying'
      }
    >
      <span
        className={cn(
          'size-[5px] shrink-0 rounded-full',
          state === 'live' ? 'dy-breathe bg-ok' : 'bg-warn',
        )}
        aria-hidden
      />
      {LABEL[state]}
    </span>
  );
}
